import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import sb from '@/utils/sb'
import { supabaseAuthActions } from '@/utils/store/supabaseAuth'
import { type ActionFunctionArgs, Link, redirect, useFetcher, useSearchParams, useNavigate } from 'react-router'
import { useEffect, useState } from 'react'

export const action = async ({ request }: ActionFunctionArgs) => {
  const supabase = sb;
  const formData = await request.formData()
  const step = formData.get('step') as string

  // 步骤1：发送验证码
  if (step === 'send-otp') {
    const email = formData.get('email') as string

    if (!email) {
      return { error: '邮箱不能为空' }
    }

    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: true,
      },
    })

    if (error) {
      return { error: error.message }
    }

    return redirect(`/s_auth/sign-up?step=verify&email=${encodeURIComponent(email)}`)
  }

  // 步骤2：验证验证码并设置密码
  if (step === 'verify-otp') {
    const email = formData.get('email') as string
    const token = formData.get('token') as string
    const password = formData.get('password') as string
    const repeatPassword = formData.get('repeat-password') as string

    if (!token) {
      return { error: '验证码不能为空' }
    }

    if (!password) {
      return { error: '密码不能为空' }
    }

    if (password !== repeatPassword) {
      return { error: '密码不一致' }
    }

    // 验证 OTP
    const { data, error: verifyError } = await supabase.auth.verifyOtp({
      email,
      token,
      type: 'email',
    })

    if (verifyError) {
      return { error: verifyError.message }
    }

    // 验证成功后设置密码
    const { error: updateError } = await supabase.auth.updateUser({ 
      password 
    })

    if (updateError) {
      return { error: updateError.message }
    }

    // 更新认证状态
    await supabaseAuthActions.update();

    return redirect('/s_auth/sign-up?success')
  }

  return { error: '无效的操作' }
}

export default function SignUp() {
  const fetcher = useFetcher<typeof action>()
  let [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [countdown, setCountdown] = useState(3)

  const success = !!searchParams.has('success')
  const step = searchParams.get('step') || 'email'
  const email = searchParams.get('email') || ''
  const error = fetcher.data?.error
  const loading = fetcher.state === 'submitting'

  // 注册成功后3秒自动跳转到首页
  useEffect(() => {
    if (success) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer)
            navigate('/', { replace: true })
            return 0
          }
          return prev - 1
        })
      }, 1000)

      return () => clearInterval(timer)
    }
  }, [success, navigate])

  return (
    <div className="flex min-h-svh w-full items-center justify-center p-6 md:p-10">
      <div className="w-full max-w-sm">
        <div className="flex flex-col gap-6">
          {success ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">注册成功！</CardTitle>
                <CardDescription>您的账户已创建并自动登录</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  您已成功完成注册，{countdown} 秒后自动跳转到首页...
                </p>
                <Button 
                  className="w-full" 
                  onClick={() => navigate('/', { replace: true })}
                >
                  立即前往首页
                </Button>
              </CardContent>
            </Card>
          ) : step === 'verify' ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">验证邮箱</CardTitle>
                <CardDescription>
                  我们已向 {email} 发送了验证码
                </CardDescription>
              </CardHeader>
              <CardContent>
                <fetcher.Form method="post">
                  <input type="hidden" name="step" value="verify-otp" />
                  <input type="hidden" name="email" value={email} />
                  <div className="flex flex-col gap-6">
                    <div className="grid gap-2">
                      <Label htmlFor="token">验证码</Label>
                      <Input
                        id="token"
                        name="token"
                        type="text"
                        placeholder="请输入6位验证码"
                        maxLength={6}
                        required
                        autoFocus
                      />
                      <p className="text-xs text-muted-foreground">
                        请查看您的邮箱获取验证码
                      </p>
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="password">密码</Label>
                      <Input 
                        id="password" 
                        name="password" 
                        type="password" 
                        placeholder="设置您的密码"
                        required 
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="repeat-password">确认密码</Label>
                      <Input 
                        id="repeat-password" 
                        name="repeat-password" 
                        type="password" 
                        placeholder="再次输入密码"
                        required 
                      />
                    </div>
                    {error && <p className="text-sm text-red-500">{error}</p>}
                    <Button type="submit" className="w-full" disabled={loading}>
                      {loading ? '验证中...' : '验证并完成注册'}
                    </Button>
                  </div>
                  <div className="mt-4 text-center text-sm">
                    <Link 
                      to="/s_auth/sign-up" 
                      className="underline underline-offset-4"
                    >
                      返回重新发送
                    </Link>
                  </div>
                </fetcher.Form>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">注册</CardTitle>
                <CardDescription>创建新账户</CardDescription>
              </CardHeader>
              <CardContent>
                <fetcher.Form method="post">
                  <input type="hidden" name="step" value="send-otp" />
                  <div className="flex flex-col gap-6">
                    <div className="grid gap-2">
                      <Label htmlFor="email">邮箱</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="m@example.com"
                        defaultValue={email}
                        required
                        autoFocus
                      />
                      <p className="text-xs text-muted-foreground">
                        我们将向此邮箱发送验证码
                      </p>
                    </div>
                    {error && <p className="text-sm text-red-500">{error}</p>}
                    <Button type="submit" className="w-full" disabled={loading}>
                      {loading ? '发送中...' : '发送验证码'}
                    </Button>
                  </div>
                  <div className="mt-4 text-center text-sm">
                    已有账户？{' '}
                    <Link to="/s_auth/login" className="underline underline-offset-4">
                      登录
                    </Link>
                  </div>
                </fetcher.Form>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}
